import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Tv, Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Stream manager — curate admin-override stream URLs per fixture_id.
 * Admin override wins over any auto-scrape.
 */
export const StreamManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fixture_id: "", url: "", home_team: "", away_team: "", notes: "",
  });

  const load = async () => {
    setLoading(true);
    try { setItems((await axios.get(`${API}/streams/admin/all`)).data.items || []); }
    catch { /* unauthed or error */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const fx = parseInt(form.fixture_id, 10);
    if (!fx || !form.url) return toast.error("Fixture ID and URL are required");
    setSaving(true);
    try {
      await axios.post(`${API}/streams/${fx}/admin`, {
        url: form.url,
        home_team: form.home_team,
        away_team: form.away_team,
        notes: form.notes,
      });
      toast.success(`Stream saved for fixture ${fx}`);
      setForm({ fixture_id: "", url: "", home_team: "", away_team: "", notes: "" });
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (fx) => {
    if (!window.confirm(`Remove admin stream for fixture ${fx}?`)) return;
    try {
      await axios.delete(`${API}/streams/${fx}/admin`);
      toast.success("Removed");
      await load();
    } catch { toast.error("Remove failed"); }
  };

  return (
    <section className="nd-card p-6" data-testid="stream-manager-card">
      <div className="flex items-center gap-3 mb-3">
        <Tv className="w-5 h-5" />
        <h2 className="font-display text-xl font-semibold">Live-stream manager</h2>
        <Badge className="bg-muted text-muted-foreground border border-border">
          {items.length} override{items.length === 1 ? "" : "s"}
        </Badge>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Curate stream URLs for specific fixtures. Admin overrides take priority over
        automatic aiscore scrapes. Paste an embeddable URL (iframe-friendly), not a homepage.
      </p>

      <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="stream-add-form">
        <div>
          <Label className="eyebrow">Fixture ID</Label>
          <Input
            type="number" value={form.fixture_id}
            onChange={(e) => setForm({ ...form, fixture_id: e.target.value })}
            placeholder="e.g. 1519046"
            className="mt-1.5 bg-subtle border-border h-11 rounded-xl"
            data-testid="stream-form-fixture"
          />
        </div>
        <div>
          <Label className="eyebrow">Stream URL</Label>
          <Input
            type="url" value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder="https://…"
            className="mt-1.5 bg-subtle border-border h-11 rounded-xl"
            data-testid="stream-form-url"
          />
        </div>
        <div>
          <Label className="eyebrow">Home team (optional)</Label>
          <Input
            value={form.home_team}
            onChange={(e) => setForm({ ...form, home_team: e.target.value })}
            className="mt-1.5 bg-subtle border-border h-11 rounded-xl"
            data-testid="stream-form-home"
          />
        </div>
        <div>
          <Label className="eyebrow">Away team (optional)</Label>
          <Input
            value={form.away_team}
            onChange={(e) => setForm({ ...form, away_team: e.target.value })}
            className="mt-1.5 bg-subtle border-border h-11 rounded-xl"
            data-testid="stream-form-away"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="eyebrow">Notes (optional)</Label>
          <Input
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="e.g. HD feed, verified 18:00 UTC"
            className="mt-1.5 bg-subtle border-border h-11 rounded-xl"
            data-testid="stream-form-notes"
          />
        </div>
        <div className="md:col-span-2">
          <Button
            type="submit"
            disabled={saving}
            data-testid="stream-save-btn"
            className="h-10 rounded-full px-5 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : "Save stream"}
          </Button>
        </div>
      </form>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading overrides…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="stream-manager-empty">
          No admin stream overrides yet.
        </p>
      ) : (
        <ul className="divide-y divide-border" data-testid="stream-manager-list">
          {items.map((it) => (
            <li key={it.fixture_id} className="py-3 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-display font-semibold flex items-center gap-2 flex-wrap">
                  <span className="font-tabular text-muted-foreground">#{it.fixture_id}</span>
                  {it.home_team && it.away_team
                    ? <span>{it.home_team} vs {it.away_team}</span>
                    : <span className="text-muted-foreground">Unnamed fixture</span>}
                  <Badge className="bg-muted text-foreground border border-border text-[10px]">
                    {it.kind}
                  </Badge>
                </div>
                <a
                  href={it.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-muted-foreground font-tabular truncate block mt-1 hover:text-foreground"
                >
                  <ExternalLink className="w-3 h-3 inline mr-1 -mt-0.5" />
                  {it.url}
                </a>
                {it.notes && <div className="text-xs text-muted-foreground mt-1">{it.notes}</div>}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => remove(it.fixture_id)}
                data-testid={`stream-remove-${it.fixture_id}`}
                className="h-8 rounded-full px-3 border-destructive/40 text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="w-3 h-3 mr-1" /> Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default StreamManager;
